import { useMemo } from 'react';
import { Gwei } from '../lib/gwei';
import { multiply, multiplyByDecimal } from '../lib/math';
import { usePositions } from './usePositions';
import { useProductPricesSub } from './useProductPricesSub';
import { useProductsById } from './useProducts';
import { useActiveSelectedSubaccount } from './useSelectedSubaccount';

export const useAccountMaintenanceMargin = () => {
  const selectedSubaccount = useActiveSelectedSubaccount();
  const {
    data: positions,
    isPending: positionPending,
    error,
  } = usePositions({ subaccountId: selectedSubaccount?.id });
  const productsById = useProductsById();
  const { data: pricesByProductId, isPending: pricesPending } = useProductPricesSub();
  const positionsWithData = useMemo(
    () =>
      positions?.map((position) => ({
        ...position,
        maintenanceMarginRatio: productsById?.[position.productId]?.maintenanceMarginRatio,
        oraclePrice: pricesByProductId[position.productId]?.oraclePrice,
      })),
    [positions, pricesByProductId, productsById]
  );

  const positionMaintenanceMargin = useMemo(
    () =>
      positionsWithData
        ?.map((position) => {
          if (!position?.oraclePrice || !position.maintenanceMarginRatio) return 0n;

          const notionalValue = Gwei.abs(multiply(position.size, position.oraclePrice));
          return multiplyByDecimal(notionalValue, position.maintenanceMarginRatio);
        })
        .reduce((acc, margin) => acc + margin, 0n),
    [positionsWithData]
  );

  return useMemo(
    () => ({
      data: positionMaintenanceMargin,
      isPending: positionPending || pricesPending,
      error,
    }),
    [error, positionMaintenanceMargin, positionPending, pricesPending]
  );
};
